import React, { useCallback, useEffect, useState } from "react";
import { SaveBar as ShopifySaveBar } from "@shopify/app-bridge-react";
import { useBlocker, useNavigate } from "react-router-dom";

export default function SaveBar({
  handleSave,
  handleDiscard,
  isDirty,
  disabled = false,
}: {
  // eslint-disable-next-line @typescript-eslint/no-redundant-type-constituents
  readonly handleSave: (event: React.MouseEvent<HTMLButtonElement>) => Promise<any> | any;
  readonly handleDiscard: () => void;
  readonly isDirty: boolean;
  readonly disabled?: boolean;
}): React.ReactElement {
  // There is a bug with SaveBar where it can't be opened before the URL changes which can sometimes take a second
  const [justOpened, setJustOpened] = useState<boolean>(true);
  useEffect(() => {
    setJustOpened(false);
  }, []);

  const [isSaving, setIsSaving] = useState<boolean>(false);
  const isOpen = isDirty && !justOpened;
  const blocker = useBlocker(isOpen && !isSaving);
  const navigate = useNavigate();

  const handleBeforeUnload = useCallback((event: BeforeUnloadEvent) => {
    const message = "Changes you made may not be saved.";
    event.returnValue = message; // For most browsers
    return message; // For some browsers
  }, []);

  useEffect(() => {
    if (isOpen) {
      window.addEventListener("beforeunload", handleBeforeUnload);
    } else {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    }

    return (): void => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [isOpen, handleBeforeUnload]);

  const reset = useCallback(() => {
    if (blocker.reset) {
      // We do this because,
      // we only prevent our embedded app to navigate not the shopify app.
      // So we force the shopify app to navigate to the current page
      navigate(`${location.pathname}${location.search}`, {
        replace: true,
      });
      blocker.reset();
    }

    handleDiscard();
  }, [blocker, handleDiscard, navigate]);

  const handleSaveAction = useCallback(
    async (event: React.MouseEvent<HTMLButtonElement>) => {
      setIsSaving(true);
      try {
        await handleSave(event);
      } finally {
        setIsSaving(false);
      }
    },
    [handleSave],
  );

  return (
    <ShopifySaveBar id="save-bar" open={isOpen}>
      <button
        disabled={disabled}
        // eslint-disable-next-line react/no-unknown-property
        loading={isSaving ? "" : undefined}
        onClick={handleSaveAction}
        type="button"
        // eslint-disable-next-line react/no-unknown-property
        variant="primary"
      />
      <button disabled={disabled || isSaving} onClick={reset} type="button" />
    </ShopifySaveBar>
  );
}
